import { CSSProperties, FC, ReactNode, useMemo } from 'react'
import cn from './index.module.css'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { KeyOutlined, MailFilled } from '@ant-design/icons'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { MaxIcon, TelegramIcon, VKIcon } from '@/utils/icons'

type UserIntegrationProvider = {
  attached?: boolean
}

type UserStatusUser = IUser & {
  vk_login_attached?: boolean
  max_chat_id?: string | number | null
  telegram_attached?: boolean | number | null
  integrations?: {
    telegram?: UserIntegrationProvider | null
    vkontakte?: UserIntegrationProvider | null
    max?: UserIntegrationProvider | null
  } | null
}

interface IUserStatus {
  user: UserStatusUser
  className?: string
  style?: CSSProperties
  children: ReactNode
  /** Иконка подтверждения почты (по умолчанию — по настройке учеников) */
  showEmailStatus?: boolean
}

interface StatusItem {
  key: string
  visible: boolean
  done: boolean
  titleDone: string
  titleNotDone: string
  icon: ReactNode
}

const isMessengerAttached = (
  integration: UserIntegrationProvider | null | undefined,
  legacyFallback?: unknown,
) => {
  if (integration?.attached != null) {
    return Boolean(integration.attached)
  }
  return Boolean(legacyFallback)
}

const UserStatus: FC<IUserStatus> = ({
  user,
  className,
  style,
  children,
  showEmailStatus,
}) => {
  const settings = useSelector((store: any) => {
    return {
      requireEmailConfirmation:
        store.settings?.requireStudentEmailConfirmation === '1',
    }
  })

  const isEmailStatusVisible =
    showEmailStatus ?? settings.requireEmailConfirmation

  const isTelegramAttached = isMessengerAttached(
    user?.integrations?.telegram,
    user?.telegram_attached,
  )
  const isVkAttached = isMessengerAttached(user?.integrations?.vkontakte)
  const isVkLoginAttached = user?.vk_login_attached === true
  const isMaxAttached = isMessengerAttached(
    user?.integrations?.max,
    user?.max_chat_id,
  )

  const statusList: StatusItem[] = useMemo(
    () => [
      {
        key: 'email',
        visible: isEmailStatusVisible,
        done: Boolean(user?.is_email_confirmed),
        titleDone: 'Почта подтверждена',
        titleNotDone: 'Почта не подтверждена',
        icon: (
          <MailFilled
            className={classNames(
              cn.statusIcon,
              Boolean(user?.is_email_confirmed) && cn.statusIconDone,
            )}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      },
      {
        key: 'tg-bot',
        visible: true,
        done: isTelegramAttached,
        titleDone: 'Telegram подключён',
        titleNotDone: 'Telegram не подключён',
        icon: (
          <TelegramIcon
            className={classNames(
              cn.statusTgIcon,
              isTelegramAttached && cn.statusIconDone,
            )}
          />
        ),
      },
      {
        key: 'vk',
        visible: true,
        done: isVkAttached,
        titleDone: 'Интеграция VK подключена',
        titleNotDone: 'Интеграция VK не подключена',
        icon: (
          <VKIcon
            className={classNames(
              cn.statusVkIcon,
              isVkAttached && cn.statusIconDone,
            )}
          />
        ),
      },
      {
        key: 'vk-login',
        visible: true,
        done: isVkLoginAttached,
        titleDone: 'Вход через VK подключён',
        titleNotDone: 'Вход через VK не подключён',
        icon: (
          <span className={classNames(cn.statusVkLoginIcon, isVkLoginAttached && cn.statusIconDone)}>
            <VKIcon className={cn.statusVkIcon} />
            <KeyOutlined
              className={cn.loginBadge}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </span>
        ),
      },
      {
        key: 'max-bot',
        visible: true,
        done: isMaxAttached,
        titleDone: 'MAX подключён',
        titleNotDone: 'MAX не подключён',
        icon: (
          <MaxIcon
            className={classNames(
              cn.statusMaxIcon,
              isMaxAttached && cn.statusIconDone,
            )}
          />
        ),
      },
    ],
    [
      isEmailStatusVisible,
      user?.is_email_confirmed,
      isTelegramAttached,
      isVkAttached,
      isVkLoginAttached,
      isMaxAttached,
    ],
  )

  return (
    <div style={style} className={classNames(cn.wrapper, className)}>
      {children}
      <div className={cn.statusList}>
        {statusList
          .filter((status) => status.visible)
          .map((status, index) => (
            <TooltipWrapper
              style={{ zIndex: statusList.length - index }}
              key={status.key}
              title={status.done ? status.titleDone : status.titleNotDone}
            >
              <div
                className={cn.icoWrapper}
                style={{ zIndex: statusList.length - index }}
              >
                {status.icon}
              </div>
            </TooltipWrapper>
          ))}
      </div>
    </div>
  )
}

export default UserStatus
